import ListFormItem from "@arelia-components/form/ListFormItem";
import { useHandleChange } from "@arelia-hooks/formikHooks";
import { useHandleFormActions } from "@arelia-hooks/formikHooks";
import { Card, CardHeader, CardContent, TextField, Typography, List, Box, FormHelperText, ButtonGroup, Button } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { useState } from "react";
import { BrandData } from "./models";

export type SettingsForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  street2: string;
  city: string;
  region: string;
  postalCode: string;
  brandName: string;
};
export const formConfig: {
  label: string;
  field: keyof SettingsForm;
  hint?: string;
}[] = [
  {
    label: "First Name",
    field: "firstName",
    hint: "The customer's first name."
  },
  {
    label: "Last Name",
    field: "lastName",
    hint: "The customer's last name."
  },
  {
    label: "Email",
    field: "email",
    hint: "This must be a well formatted address with a valid domain and cannot be a disposable address."
  },
  {
    label: "Phone",
    field: "phone",
    hint: "The customer's business/contact phone number. It must be a well-formatted number and can be a landline, mobile, or other number."
  },
  {
    label: "Street Address",
    field: "street",
    hint: "The customer's street address. This field cannot be a P.O. Box."
  },
  {
    label: "Street Second Line",
    field: "street2",
    hint: "The customer's street address, continued. This field can be a P.O. Box."
  },
  {
    label: "City",
    field: "city",
    hint: "The customer's city."
  },
  {
    label: "Region",
    field: "region",
    hint: "The customer's region(state)."
  },
  {
    label: "Postal Code",
    field: "postalCode",
    hint: "The customer's zip code."
  },
  {
    label: "Brand",
    field: "brandName",
    hint: "Customer's real business name. If you are not a business entity but instead are a hobbyist / college student, etc., please provide first name and last name. This field cannot be a unique identifier such as an account ID or email address."
  }
];

export type Props = {
  onEdit: (data: BrandData) => void;
  data: BrandData;
  editing: boolean;
};

export default function SettingsPresenter({ onEdit, data, editing }: Props) {

  const handleSubmit = () => {
    onEdit(formik.values);
  };

  const formik: FormikProps<SettingsForm> = useFormik<SettingsForm>({
    initialValues: {
      firstName: data?.firstName ?? "",
      lastName: data?.lastName ?? "",
      email: data?.email ?? "",
      phone: data?.phone ?? "",
      street: data?.street ?? "",
      street2: data?.street2 ?? "",
      city: data?.city ?? "",
      region: data?.region ?? "",
      postalCode: data?.postalCode ?? "",
      brandName: data?.brandName ?? "",

    },
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });
  const handeChange = useHandleChange<SettingsForm>(formik);

  const readFieldRenderer = (field: keyof SettingsForm) => {
    const value = formik.values[field];
    return <Typography variant="subtitle2">{value}</Typography>;
  }

  const editFieldRenderer = (field: keyof SettingsForm, hint?: string) => {
    const value = formik.values[field];

    return <Box>
      <TextField
      value={value || ""}
      onChange={handeChange(field)}
      size="small"
      type="text"
      fullWidth
      />
      { !!hint && <FormHelperText>
        {hint}
      </FormHelperText>}
    </Box>;
  }
  const sendDisabled = formConfig
    .filter(({ field }) => field != "street2")
    .map(({ field }) => formik.values[field])
    .some((value) => !value);
  
  return (
    <Card sx={styles.card}>
      <CardHeader
        title="Customer info"
      />
      <CardContent sx={{
        justifyContent: "flex-start"
      }}>
        <List>
          {formConfig.map(({ label, field, hint }, index) => (
            <ListFormItem key={index} label={label}>
              {editing ? editFieldRenderer(field, hint) : readFieldRenderer(field)}
            </ListFormItem>
          ))}
        </List>
        {editing && <Box sx={styles.controls}>
          <Button variant="contained" onClick={handleSubmit} disabled={sendDisabled}>Register Your Profile</Button>
        </Box>}
      </CardContent>
    </Card>
  );
}

const styles = {
  card: {
    
  },
  cardContent: {
    justifyContent: "flex-start"
  },
  controls: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "1rem"
  },
};

